import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { EyeIcon, EyeOffIcon, WifiOffIcon } from '../icons';

type Props = { onLogin: () => void; restoreError: boolean; offline: boolean };

function loginMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Something unexpected happened. Please try again.';
  if (error.status === 401) return 'That username or password is not recognised.';
  if (error.status === 429) return 'Too many sign-in attempts. Please wait 15 minutes before trying again.';
  if (error.status === 0) return 'We could not reach the server. Check your connection and try again.';
  return error.message;
}

export function LoginPage({ onLogin, restoreError, offline }: Props) {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const mutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) => api.login(username, password),
    onSuccess(user) {
      queryClient.setQueryData(['session'], user);
      onLogin();
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (offline || mutation.isPending) return;
    const values = new FormData(event.currentTarget);
    const username = String(values.get('username') ?? '').trim();
    const password = String(values.get('password') ?? '');
    if (!username || !password) return;
    mutation.mutate({ username, password });
  }

  function togglePassword() { setShowPassword((shown) => !shown); }
  function detectCapsLock(event: KeyboardEvent<HTMLInputElement>) { setCapsLock(event.getModifierState('CapsLock')); }

  const message = mutation.isError
    ? loginMessage(mutation.error)
    : offline
      ? 'You’re offline. Reconnect to sign in.'
      : restoreError
        ? 'Your previous session could not be restored. Please sign in again.'
        : '';

  return (
    <main className="login-page">
      <section className="login-story" aria-hidden="true">
        <div className="story-content">
          <span className="story-kicker">Sprout &amp; About</span>
          <h2>Every shelf,<br />beautifully tended.</h2>
          <p>Quick, calm stock control for a growing business.</p>
        </div>
        <div className="leaf leaf-one" /><div className="leaf leaf-two" /><div className="leaf leaf-three" />
      </section>
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-card">
          <div className="login-brand"><span className="brand-mark">S</span><span>Sprout &amp; About</span></div>
          <div className="login-heading">
            <span className="eyebrow">Inventory manager</span>
            <h1 id="login-title">Welcome back</h1>
            <p>Sign in to tend your stock.</p>
          </div>
          <form onSubmit={handleSubmit} noValidate={false}>
            <label className="field">
              <span>Username</span>
              <input name="username" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} required disabled={mutation.isPending} />
            </label>
            <div className="field">
              <label htmlFor="login-password">Password</label>
              <span className="password-field">
                <input id="login-password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required disabled={mutation.isPending} onKeyUp={detectCapsLock} onKeyDown={detectCapsLock} aria-describedby="password-note" />
                <button type="button" className="icon-button" onClick={togglePassword} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </span>
            </div>
            <div id="password-note" className="field-note" aria-live="polite">{capsLock ? 'Caps Lock is on' : '\u00a0'}</div>
            <div className={`login-alert ${message ? 'visible' : ''}`} role="alert" aria-live="assertive">
              {offline && <WifiOffIcon />}{message || '\u00a0'}
            </div>
            <button className="primary-button login-button" type="submit" disabled={mutation.isPending || offline}>
              {mutation.isPending ? <><span className="spinner small" />Signing in…</> : 'Sign in'}
            </button>
          </form>
          <p className="login-footnote">Private workspace · Secure session</p>
        </div>
      </section>
    </main>
  );
}
